import { get, isEmpty, toPairs, fromPairs, isArray, isObject } from 'lodash';
import { useI18nContext } from '@tmsw/powerups.intl.provider';

function interpolate(input: any, options) {
  const { replacements, activeNS, ctx } = options;
  let output = input;

  if (isArray(output)) {
    // console.log('Processing array', output);
    output = output.map((value) => interpolate(value, options));
  } else if (isObject(output)) {
    // console.log('Processing object', output);
    const pairs = toPairs(output).map(([key, val]) => [
      key,
      interpolate(val, options),
    ]);
    output = fromPairs(pairs);
  }

  if (typeof output !== 'string') return output;

  // if (typeof output === 'string') {
  //   output = output.replace('./static/', 'https://localhost:3001/static/');
  // }

  const varMatches = output.match(new RegExp(`{{(.*?)}}`, 'gi'));
  // console.log(
  //   'interpolate',
  //   'output=',
  //   output,
  //   'varMatches=',
  //   varMatches,
  //   'ctx=',
  //   ctx,
  //   'activeNS=',
  //   activeNS
  // );

  if (!varMatches || !Array.isArray(varMatches)) return output;

  varMatches.forEach((key) => {
    const keyName = key.replace('{{', '').replace('}}', '');
    // console.log('key, keyName', key, keyName);

    let value =
      typeof replacements[keyName] !== 'undefined'
        ? replacements[keyName]
        : null;

    if (!value) {
      // console.log('interpolation :: trying', `${activeNS}.${keyName}`);
      value = get(ctx, `${activeNS}.${keyName}`);
      // console.log('found ', value);
    }
    if (!value) {
      // console.log('interpolation :: trying', `translations.${keyName}`);
      value = get(ctx, `${ctx.defaultNS}.${keyName}`);
      // console.log('found ', value);
    }
    if (!value) {
      value = `[${keyName}]`;
    }
    output = output.replace(key, value);
  });

  // console.log('interpolation result: ', output);
  return interpolate(output, { replacements, activeNS, ctx });
}

// error TS1016: A required parameter cannot follow an optional parameter.
// workaround for pattern __t(undefined, ...args)

/**
 * @typedef {Object} TProps - The pure `_t` function that `__t` calls
 * @property {string} key - a string property of SpecialType
 * @property {number} prop2 - a number property of SpecialType
 * @property {number=} prop3 - an optional number property of SpecialType
 * @prop {number} [prop4] - an optional number property of SpecialType
 * @prop {number} [prop5=42] - an optional number property of SpecialType with default
 */
export declare type TProps = {
  key: string;
  ctx: any;
  ns: string;
  options: {
    overrideNS?: string;
    defaultToKey: boolean;
    dynamicKey: boolean;
    defaultValue?: any;
    [key: string]: unknown; // replacement key => val
  };
};

function _t({ key, ctx, ns, options }: TProps) {
  // console.group(`_t({${key}, [ctx], ${ns}, ${JSON.stringify(options)}})`);
  const { overrideNS, dynamicKey, defaultToKey, ...replacements } = options;

  let { defaultValue } = options;

  // Provide Legacy support for transient lensing `translate(key, parentNode)`
  const activeNS = typeof overrideNS === 'string' ? overrideNS : ns;
  const interpolateOptions = {
    replacements,
    activeNS,
    ctx,
  };

  /**
   * A dynamicKey is its own defaultValue;
   *
   */
  if (dynamicKey) {
    defaultValue = key;
  } else {
    defaultValue =
      defaultValue ??
      (defaultToKey ? `${activeNS}.${key}`.replace('translations.', '') : null);
  }
  // console.log(key, defaultValue);
  defaultValue = defaultValue
    ? interpolate(defaultValue, interpolateOptions)
    : null;

  // lens into the translation object
  const dict = get(ctx, activeNS);

  if (typeof dict === 'undefined') {
    /* eslint-disable-next-line no-console */
    if (!defaultValue && !dynamicKey) {
      console.error('Translation Missing', key, activeNS);
    }
  }

  let returnVal = defaultValue;

  if (options.overrideNS && dict[options.overrideNS]?.hasOwnProperty(key)) {
    returnVal = dict[options.overrideNS][key];
    // console.log('Using overrideNS', key, options.overrideNS, returnVal);
  } else if (dict.hasOwnProperty(key)) {
    returnVal = get(dict, key, defaultValue);
  } else {
    returnVal = get(dict, key, defaultValue);
  }

  // if (typeof returnVal === 'string') {
  returnVal = interpolate(returnVal, interpolateOptions);
  // } else if (isArray(returnVal)) {
  //   // returnVal = returnVal;
  //   // TODO
  // } else if (isObject(returnVal)) {
  //   returnVal = toPairs(returnVal).map(([key, val]) => [
  //     key,
  //     interpolate(val, interpolateOptions),
  //   ])
  //   returnVal = fromPairs(returnVal);
  // }

  // console.groupEnd();
  return returnVal;
}

/**
 * @name useTranslation
 * @description Genera
 * @param args
 * @returns
 */
export function useTranslation(...args) {
  const ctx = useI18nContext();
  if (!ctx) throw new Error('Missing I18nContext.Provider in the tree');

  // set the ns to the first arg or use the default
  let ns = args[0] && typeof args[0] === 'string' ? args[0] : ctx.defaultNS;

  const baseOptions = {
    ...{ defaultToKey: false, dynamicKey: false },
    ...(typeof args[0] === 'object' ? args[0] : {}),
    ...(typeof args[1] === 'object' ? args[1] : {}),
  };

  // console.log('useTranslation', baseOptions);

  // prepend the default ns
  if (ns !== ctx.defaultNS) {
    ns = `${ctx.defaultNS}.${ns}`;
  }

  const __t = (...tArgs: any) => {
    const key = tArgs[0];

    const options = {
      ...baseOptions,
      ...(typeof tArgs[1] === 'object' ? tArgs[1] : {}),
      // override namespace for this call only
      // provides legacy support for `translate(parentNode)`
      ...(typeof tArgs[1] === 'string'
        ? { overrideNS: `${ctx.defaultNS}.${tArgs[1]}` }
        : {}),
      ...(typeof tArgs[2] === 'object' && !isEmpty(tArgs[2]) ? tArgs[2] : {}),
    };

    // console.log(tArgs, key, options);

    const res = _t({
      key,
      ctx,
      ns,
      options,
    });

    // console.log(res, res, typeof res);
    return res;
  };

  return { __t, ctx };
}

export default useTranslation;
